import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import api from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { Users, CalendarCheck, TrendingUp, XCircle, CalendarClock, ShieldAlert, BarChart3, CalendarDays } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";

const STATUS_COLORS = {
  "Scheduled": "#94a3b8",
  "Waiting": "#f59e0b",
  "With Doctor": "#0ea5e9",
  "Completed": "#16a34a",
  "Follow-up Scheduled": "#7c3aed",
  "Cancelled": "#dc2626",
};
const TYPE_COLORS = { "New Visit": "#0f766e", "Follow-up": "#7c3aed", "Emergency": "#dc2626" };

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/doctor/analytics").then(({ data }) => setData(data));
  }, []);

  if (!data) return <DoctorLayout><p className="text-slate-500">Loading analytics...</p></DoctorLayout>;

  const statusChartData = (data.status_breakdown || []).map((s) => ({ name: s.status, value: s.count, fill: STATUS_COLORS[s.status] || "#94a3b8" }));
  const typeChartData = (data.type_breakdown || []).map((t) => ({ name: t.type, count: t.count, fill: TYPE_COLORS[t.type] || "#94a3b8" }));
  const weekdayData = data.weekday_distribution || [];
  const totalStatusCount = statusChartData.reduce((s, e) => s + e.value, 0);
  const busiestDay = weekdayData.reduce((max, d) => (d.count > (max?.count ?? -1) ? d : max), null);
  const monthlyTrendData = (data.monthly_trend || []).map((m) => ({
    ...m,
    completion_rate: m.total > 0 ? Math.round((m.completed / m.total) * 100) : 0,
  }));
  const missedGaugeData = [{ name: "missed", value: data.missed_appointment_percentage, fill: data.missed_appointment_percentage > 20 ? "#dc2626" : data.missed_appointment_percentage > 10 ? "#f59e0b" : "#16a34a" }];

  return (
    <DoctorLayout>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="analytics-heading">Doctor Analytics</h1>
      <p className="mt-1 text-sm text-slate-600">Practice performance and appointment trends — computed directly from your clinic's records, zero AI cost.</p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard testId="analytics-total-patients" label="Total Patients" value={data?.total_patients ?? "—"} icon={Users} onClick={() => navigate("/doctor/patients")} />
        <KpiCard testId="analytics-completed" label="Completed Consultations" value={data?.completed_consultations ?? "—"} icon={CalendarCheck} onClick={() => navigate("/doctor/queue?status=COMPLETED&scope=all")} />
        <KpiCard testId="analytics-followups-due" label="Follow-ups Due" value={data?.followups_due ?? "—"} icon={CalendarClock} onClick={() => navigate("/doctor/queue?status=FOLLOW_UP_SCHEDULED&scope=all")} />
        <KpiCard testId="analytics-cancelled" label="Cancelled Appointments" value={data?.cancelled_appointments ?? "—"} icon={XCircle} accent="text-amber-500" onClick={() => navigate("/doctor/queue?status=CANCELLED&scope=all")} />
        <KpiCard testId="analytics-missed-pct" label="Missed Appointment %" value={data ? `${data.missed_appointment_percentage}%` : "—"} icon={ShieldAlert} accent="text-red-600" onClick={() => navigate("/doctor/queue?status=CANCELLED&scope=all")} />
        <KpiCard testId="analytics-total-appointments" label="Total Appointments" value={data?.total_appointments ?? "—"} icon={TrendingUp} onClick={() => navigate("/doctor/queue?scope=all")} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6 lg:col-span-2" data-testid="chart-monthly-trend">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Appointments — Last 6 Months</h2>
          <div className="mt-4 h-80">
            {monthlyTrendData.some((m) => m.total > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyTrendData}>
                  <defs>
                    <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f766e" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#0f766e" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                  <Legend verticalAlign="top" height={30} />
                  <Area yAxisId="count" type="monotone" dataKey="total" name="Total" stroke="#0f766e" strokeWidth={2.5} fill="url(#totalGrad)" dot={{ r: 4, fill: "#0f766e" }} />
                  <Line yAxisId="count" type="monotone" dataKey="cancelled" name="Cancelled" stroke="#dc2626" strokeWidth={2} dot={{ r: 3, fill: "#dc2626" }} />
                  <Line yAxisId="rate" type="monotone" dataKey="completion_rate" name="Completion Rate %" stroke="#7c3aed" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: "#7c3aed" }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">No appointment data recorded yet in the last 6 months.</p>
            )}
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-missed-gauge">
          <h2 className="font-semibold text-slate-900">Missed Appointment Rate</h2>
          <div className="relative mt-4 flex h-64 items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={missedGaugeData} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "#f1f5f9" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-900">{data.missed_appointment_percentage}%</span>
              <span className="text-xs text-slate-400">of appointments</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-status-breakdown">
          <h2 className="font-semibold text-slate-900">Appointment Status Breakdown</h2>
          <div className="relative mt-4 flex h-64 items-center justify-center">
            {statusChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusChartData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} label={({ percent }) => `${Math.round(percent * 100)}%`} labelLine={false}>
                      {statusChartData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={50} wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute top-[38%] flex flex-col items-center">
                  <span className="text-2xl font-bold text-slate-900">{totalStatusCount}</span>
                  <span className="text-[10px] text-slate-400">Total</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400">No appointments recorded yet.</p>
            )}
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-type-breakdown">
          <h2 className="font-semibold text-slate-900">New Visit vs Follow-up vs Emergency</h2>
          <div className="mt-4 h-64">
            {typeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={typeChartData} layout="vertical" margin={{ left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} width={90} />
                  <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                    {typeChartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">No appointments recorded yet.</p>
            )}
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="chart-weekday-distribution">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Busiest Clinic Days</h2>
          {busiestDay?.count > 0 && <p className="mt-1 text-xs text-slate-500">Peak day: <span className="font-medium text-teal-700">{busiestDay.day}</span> ({busiestDay.count} appointments, all-time)</p>}
          <div className="mt-3 h-56">
            {weekdayData.some((d) => d.count > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekdayData}>
                  <defs>
                    <linearGradient id="weekdayGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0d9488" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0d9488" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} axisLine={{ stroke: "#e2e8f0" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0" }} />
                  <Bar dataKey="count" fill="url(#weekdayGrad)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="flex h-full items-center justify-center text-sm text-slate-400">No appointments recorded yet.</p>
            )}
          </div>
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-400">Clinical Outcome Trends and Treatment Outcome Trends will be available once AI Report Analysis (Phase 2) is live.</p>
    </DoctorLayout>
  );
}
